// client/src/components/pages/LexiStar/Approvals/useRouteGeometries.js
//
// Planned-route geometry and corridor polygons for the maps on the approvals
// queue and trip detail, from the M2-A1 routes API (m2-contracts §4.1):
// GET /routes/:id (+versions → the pinned version's LineString) and
// GET /routes/:id/versions/:v/corridor (display polygon). Cached per
// route:version for the session; failures leave the map without that layer
// (the page says so) rather than failing the page.

import { useEffect, useState } from 'react';
import { getRoute, getRouteCorridor, pickList } from '../../../../services/lexistarStatusBoard';

const lineCache = new Map();
const corridorCache = new Map();

/** Clear the module cache (tests). */
export const resetRouteGeometryCache = () => {
  lineCache.clear();
  corridorCache.clear();
};

/** Feature | Polygon geometry | { corridor } | FeatureCollection → a Feature or null. */
export function toFeature(x) {
  if (!x || typeof x !== 'object') return null;
  if (x.corridor) return toFeature(x.corridor);
  if (x.type === 'Feature') return x.geometry ? x : null;
  if (x.type === 'FeatureCollection') return x.features?.[0] || null;
  if (x.type === 'Polygon' || x.type === 'MultiPolygon' || x.type === 'LineString') {
    return { type: 'Feature', properties: {}, geometry: x };
  }
  if (x.geometry) return toFeature(x.geometry);
  return null;
}

async function loadLine(routeId, version) {
  const key = `${routeId}:${version}`;
  if (!lineCache.has(key)) {
    lineCache.set(
      key,
      getRoute(routeId)
        .then((res) => {
          const versions = pickList(res, 'versions');
          const v =
            versions.find((x) => Number(x.version) === Number(version)) ||
            versions.find((x) => Number(x.version) === Number(res.route?.current_version)) ||
            versions[versions.length - 1];
          return toFeature(v?.geometry);
        })
        .catch((err) => {
          lineCache.delete(key);
          throw err;
        })
    );
  }
  return lineCache.get(key);
}

async function loadCorridor(routeId, version) {
  const key = `${routeId}:${version}`;
  if (!corridorCache.has(key)) {
    corridorCache.set(
      key,
      getRouteCorridor(routeId, version)
        .then(toFeature)
        .catch((err) => {
          corridorCache.delete(key);
          throw err;
        })
    );
  }
  return corridorCache.get(key);
}

/**
 * @param {Array<{route_id:string, route_version:number}>} refs pinned route versions
 * @param {{corridor?:boolean}} opts
 * @returns {{lines:Array<{key:string, feature:Object}>, corridors:Array<{key:string, feature:Object}>,
 *            loading:boolean, failed:boolean}}
 */
export default function useRouteGeometries(refs, { corridor = true } = {}) {
  const wanted = (refs || []).filter((r) => r && r.route_id && r.route_version != null);
  const signature = wanted.map((r) => `${r.route_id}:${r.route_version}`).join('|');
  const [state, setState] = useState({ lines: [], corridors: [], loading: false, failed: false });

  useEffect(() => {
    if (!signature) {
      setState({ lines: [], corridors: [], loading: false, failed: false });
      return undefined;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true, failed: false }));
    const pairs = signature.split('|').map((k) => k.split(':'));
    Promise.allSettled(
      pairs.flatMap(([id, v]) => [
        loadLine(id, v).then((feature) => ({ kind: 'line', key: `${id}:${v}`, feature })),
        ...(corridor
          ? [
              loadCorridor(id, v).then((feature) => ({
                kind: 'corridor',
                key: `${id}:${v}`,
                feature,
              })),
            ]
          : []),
      ])
    ).then((results) => {
      if (!alive) return;
      const ok = results
        .filter((r) => r.status === 'fulfilled' && r.value.feature)
        .map((r) => r.value);
      setState({
        lines: ok.filter((r) => r.kind === 'line'),
        corridors: ok.filter((r) => r.kind === 'corridor'),
        loading: false,
        failed: results.some((r) => r.status === 'rejected'),
      });
    });
    return () => {
      alive = false;
    };
  }, [signature, corridor]);

  return state;
}
